import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { RouteConstants } from '../../../core/constants/routes';
import { ApplicationState } from '../../../core/models/application-state/application-state';
import { FormGroupType } from '../../../core/models/form-group-type';
import { RegExPattern } from '../../../core/models/regexp-pattern/regex-pattern';
import { StringValidators } from '../../../core/validators/string-validators/string-validators';
import { RegexInputDirective } from '../../../shared/directives/regex-input/regex-input.directive';
import { SubmitToEwaFormService } from '../direct-entry/submit-to-ewa/components/shared/services/submit-to-ewa-form.service';
import { ApplicationDeletionService } from '../shared/services/application-deletion/application-deletion.service';
import { ApplicationEventActionService } from '../shared/services/application-event-action/application-event-action.service';
import { ApplicationStateService } from '../shared/services/application-state/application-state.service';
import { StepNavigationService } from '../shared/services/step-navigation/step-navigation.service';
import { UnsupportedApplicationService } from '../shared/services/unsupported-application/unsupported-application.service';
import { UnsupportedApplicationFormModel } from './models/unsupported-application-form-model';
import { UnsupportedApplicationResponse } from './models/unsupported-application-response';

@Component({
    selector: 'app-unsupported-application',
    standalone: true,
    templateUrl: './unsupported-application.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatTooltipModule,
        RegexInputDirective,
    ],
})
export class UnsupportedApplicationComponent implements OnInit, OnDestroy {
    applicationId: string | undefined;
    applicationState: ApplicationState | null =
        this.applicationStateService.getCurrentApplicationState();
    hasChanges: boolean = false;
    showSubmitButton = true;
    regExPattern = RegExPattern;

    readonly commentsMaxLength = 1000;

    form: FormGroup<FormGroupType<UnsupportedApplicationFormModel>> =
        new FormGroup({
            comments: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(this.commentsMaxLength),
                StringValidators.noWhitespace,
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
        });

    private destroy$ = new Subject<void>();

    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly unsupportedApplicationService: UnsupportedApplicationService,
        private readonly applicationStateService: ApplicationStateService,
        private readonly stepNavigationService: StepNavigationService,
        private readonly applicationEventActionService: ApplicationEventActionService,
        private readonly applicationDeletionService: ApplicationDeletionService,
        private readonly submitToEwaFormService: SubmitToEwaFormService,
    ) {}

    ngOnInit(): void {
        const routeId = this.route.parent?.snapshot?.paramMap.get('id');
        if (!routeId) {
            throw new Error('Application ID is required.');
        }
        this.applicationId = routeId;

        this.loadForm()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.form.valueChanges
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(() => {
                        this.hasChanges = this.form.dirty;
                    });

                this.handleSaveAndExitClick();
                this.handleDeleteClick();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    enableSubmission(): boolean {
        return this.form.valid && !this.hasBeenHandedOverToEwa();
    }

    hasBeenHandedOverToEwa(): boolean {
        return !!this.applicationState?.handoverToEwa;
    }

    submitToEwa(): void {
        if (!this.enableSubmission()) {
            this.form.markAllAsTouched();
            return;
        }

        const comments = this.form.controls.comments.value!.trim();
        const request = {
            applicationId: this.applicationId!,
            comments,
        };

        this.submitToEwaFormService.handoverToEwaSubmission(
            () =>
                this.unsupportedApplicationService
                    .submitUnsupportedApplicationToEwa(request)
                    .pipe(map(() => void 0)),
            () => {
                this.hasChanges = false; // Prevent the unsaved changes guard from blocking the navigation
            },
        );
    }

    private onSaveAndClose(): void {
        // If no changes, just navigate to dashboard
        if (this.form.pristine) {
            this.navigateToDashboard();
            return;
        }

        const comments = this.form.controls.comments.value?.trim() ?? null;

        this.unsupportedApplicationService
            .updateUnsupportedApplication(this.applicationId!, { comments })
            .subscribe({
                next: () => {
                    this.hasChanges = false;
                    this.navigateToDashboard();
                },
                error: (err) => {
                    throw new Error('Error while saving comments: ' + err);
                },
            });
    }

    private loadForm(): Observable<void> {
        return this.getFormDataFromApi(this.applicationId!).pipe(
            tap((response: UnsupportedApplicationResponse) =>
                this.onLoad(response),
            ),
            map(() => void 0),
        );
    }

    private onLoad(response: UnsupportedApplicationResponse): void {
        this.bindForm(response);

        if (this.hasBeenHandedOverToEwa()) {
            this.form.disable({ emitEvent: false });
            this.showSubmitButton = !this.hasBeenHandedOverToEwa();
        }
    }

    private bindForm(response: UnsupportedApplicationResponse): void {
        this.form.patchValue(response);
    }

    private getFormDataFromApi(
        applicationId: string,
    ): Observable<UnsupportedApplicationResponse> {
        return this.unsupportedApplicationService.getUnsupportedApplication(
            applicationId,
        );
    }

    private handleSaveAndExitClick(): void {
        this.stepNavigationService.saveAndClose$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onSaveAndClose());
    }

    private handleDeleteClick(): void {
        this.applicationEventActionService.deleteClick$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                // Prevent the “unsaved changes” guard from blocking the delete navigation
                this.hasChanges = false;

                this.applicationDeletionService.deleteApplication(
                    this.applicationId!,
                );
            });
    }

    private navigateToDashboard(): void {
        this.router.navigate([RouteConstants.Dashboard.getFullRoute('INDEX')]);
    }
}
